import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Region } from '@internal/backstage-plugin-bidone-backend';
import { StepRichTextEditor } from './StepRichTextEditor';

const RepoField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const RegionsBlock = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const RegionsLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

const RegionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

interface Props {
  open: boolean;
  // Initial HTML to load into the editor whenever the dialog opens. The
  // draft is held internally so Cancel discards changes without touching
  // the parent's state.
  initialHtml: string;
  // Canonical region order shown as a checkbox row below the editor.
  regions: readonly Region[];
  // Which regions are currently selected on the row being edited. New
  // rows pass an array containing every region (so "all checked" reads
  // as the default).
  initialSelectedRegions: readonly Region[];
  // Bitbucket repo currently attached to the row, or null for a manual
  // (non-repo) row. Setting a value here turns the row into a repo-row
  // so build/state/owner/region data flows in from Azure.
  initialRepo: string | null;
  // Label for the dialog title — typically "Edit step" / "Add manual step".
  title?: string;
  onSave: (html: string, regions: Region[], repo: string | null) => void;
  onCancel: () => void;
}

// Modal RTE wrapper + region picker. Only one TipTap instance is alive at
// a time (the dialog either mounts or it doesn't) so we don't pay the
// per-step mount cost we used to pay in the old list-based edit UI.
export const StepEditorDialog: React.FC<Props> = ({
  open,
  initialHtml,
  regions,
  initialSelectedRegions,
  initialRepo,
  title = 'Edit step',
  onSave,
  onCancel,
}) => {
  const [draft, setDraft] = useState(initialHtml);
  const [selected, setSelected] = useState<Set<Region>>(
    () => new Set(initialSelectedRegions),
  );
  const [repo, setRepo] = useState(initialRepo ?? '');

  // Reset draft, selection, and repo each time the dialog reopens for a
  // different row. Keying on `open` lets the user re-edit the same row
  // and discard mid-flight changes via Cancel.
  useEffect(() => {
    if (open) {
      setDraft(initialHtml);
      setSelected(new Set(initialSelectedRegions));
      setRepo(initialRepo ?? '');
    }
  }, [open, initialHtml, initialSelectedRegions, initialRepo]);

  const toggle = (r: Region) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <StepRichTextEditor value={draft} onChange={setDraft} />
        <RepoField
          label="Repo name"
          placeholder="Bitbucket repo (leave blank for a manual row)"
          value={repo}
          onChange={e => setRepo(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          helperText="When set, the row pulls build, owner, and per-region tags from Azure."
        />
        <RegionsBlock>
          <RegionsLabel variant="caption">
            Select region(s) this step applies to
          </RegionsLabel>
          <RegionsRow>
            {regions.map(r => (
              <FormControlLabel
                key={r}
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={selected.has(r)}
                    onChange={() => toggle(r)}
                  />
                }
                label={r}
              />
            ))}
          </RegionsRow>
        </RegionsBlock>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() =>
            onSave(
              draft,
              regions.filter(r => selected.has(r)),
              repo.trim() || null,
            )
          }
          color="primary"
          variant="contained"
        >
          Save step
        </Button>
      </DialogActions>
    </Dialog>
  );
};
